import { memo, useMemo } from 'react';
import { useWeatherSystem } from '@/hooks/useWeatherSystem';

const RainEffect = memo(({ intensity, opacity }: { intensity: number; opacity: number }) => {
  const drops = useMemo(() => 
    Array.from({ length: Math.floor(50 * intensity) }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 2}s`,
      animationDuration: `${0.5 + Math.random() * 0.5}s`,
      opacity: 0.3 + Math.random() * 0.4,
    })),
  [intensity]);

  return (
    <div 
      className="fixed inset-0 pointer-events-none z-[5] overflow-hidden"
      style={{ opacity }}
    >
      {drops.map(drop => (
        <div
          key={drop.id}
          className="absolute w-px bg-gradient-to-b from-transparent via-muted-foreground/20 to-transparent animate-rain"
          style={{
            left: drop.left,
            top: '-20px',
            height: '20px',
            animationDelay: drop.animationDelay,
            animationDuration: drop.animationDuration,
            opacity: drop.opacity,
          }}
        />
      ))}
      <style>{`
        @keyframes rain {
          0% { transform: translateY(0); }
          100% { transform: translateY(100vh); }
        }
        .animate-rain {
          animation: rain linear infinite;
        }
      `}</style>
    </div>
  );
});
RainEffect.displayName = 'RainEffect';

const FogEffect = memo(({ intensity, opacity }: { intensity: number; opacity: number }) => (
  <div 
    className="fixed inset-0 pointer-events-none z-[5]"
    style={{ opacity }}
  >
    <div 
      className="absolute inset-0 bg-gradient-to-b from-background/40 via-transparent to-background/40"
      style={{ opacity: intensity * 0.6 }}
    />
    <div 
      className="absolute inset-0"
      style={{
        background: `radial-gradient(ellipse at 50% 50%, transparent 0%, hsl(var(--background) / ${intensity * 0.3}) 100%)`,
      }}
    />
  </div>
));
FogEffect.displayName = 'FogEffect';

const SnowEffect = memo(({ intensity, opacity }: { intensity: number; opacity: number }) => {
  const flakes = useMemo(() => 
    Array.from({ length: Math.floor(40 * intensity) }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      size: 2 + Math.random() * 4,
      animationDelay: `${Math.random() * 5}s`,
      animationDuration: `${4 + Math.random() * 4}s`,
      opacity: 0.4 + Math.random() * 0.4,
    })),
  [intensity]);

  return (
    <div 
      className="fixed inset-0 pointer-events-none z-[5] overflow-hidden"
      style={{ opacity }}
    >
      {flakes.map(flake => (
        <div
          key={flake.id}
          className="absolute rounded-full bg-foreground/20 animate-snow"
          style={{
            left: flake.left,
            top: '-10px',
            width: `${flake.size}px`,
            height: `${flake.size}px`,
            animationDelay: flake.animationDelay,
            animationDuration: flake.animationDuration,
            opacity: flake.opacity,
          }}
        />
      ))}
      <style>{`
        @keyframes snow {
          0% { 
            transform: translateY(0) translateX(0); 
          }
          25% {
            transform: translateY(25vh) translateX(10px);
          }
          50% {
            transform: translateY(50vh) translateX(-10px);
          }
          75% {
            transform: translateY(75vh) translateX(5px);
          }
          100% { 
            transform: translateY(100vh) translateX(0); 
          }
        }
        .animate-snow {
          animation: snow linear infinite;
        }
      `}</style>
    </div>
  );
});
SnowEffect.displayName = 'SnowEffect';

const WeatherOverlay = () => {
  const { weather, intensity, transitionProgress, isActive } = useWeatherSystem();

  if (!isActive) return null;

  const opacity = transitionProgress;

  return (
    <>
      {weather === 'rain' && <RainEffect intensity={intensity} opacity={opacity} />}
      {weather === 'fog' && <FogEffect intensity={intensity} opacity={opacity} />}
      {weather === 'snow' && <SnowEffect intensity={intensity} opacity={opacity} />}
    </>
  );
};

export default memo(WeatherOverlay);
